import { type MoneyMapAccountWithAccountDetails } from '@fruitful/types';
import { useAtomValue } from 'jotai/index';
import { createContext, useContext } from 'react';
import { type ContainerComponent, FullScreenSpinner } from 'smoothie';

import { selectedMembershipAtom } from '../../entities/entityAtoms';
import { useGetMembershipMoneyMapAccounts } from '../hooks/useGetMembershipMoneyMapAccounts';

interface AccountContext {
    accounts: MoneyMapAccountWithAccountDetails[];
    isLoading: boolean;
    refetch: () => Promise<void>;
}

const MoneyMapAccountContext = createContext<AccountContext>({
    accounts: [],
    isLoading: true,
    refetch: async () => {},
});

export const useMoneyMapAccounts = (): AccountContext => {
    const contextValue = useContext(MoneyMapAccountContext);

    if (contextValue == null) {
        throw new Error(
            'useMoneyMapAccounts must be used within MoneyMapAccountsProvider',
        );
    }

    return contextValue;
};

export const MoneyMapAccountsProvider: ContainerComponent = ({ children }) => {
    const selectedMembership = useAtomValue(selectedMembershipAtom);
    const membershipId = selectedMembership?.id ?? null;
    const accountsQueryEnabled = membershipId != null;

    const {
        data: moneyMapAccounts,
        isLoading: isLoadingAccounts,
        refetch: refetchMoneyMapAccounts,
    } = useGetMembershipMoneyMapAccounts(
        { membershipId: membershipId ?? '' },
        { enabled: accountsQueryEnabled },
    );

    const refetch = async () => {
        await refetchMoneyMapAccounts();
    };

    // Block until membership is known and accounts have loaded. While the query is disabled
    // (`membershipId` missing), v5 reports `isLoading` false — `!accountsQueryEnabled` covers that.
    if (!accountsQueryEnabled || isLoadingAccounts) {
        return <FullScreenSpinner />;
    }

    return (
        <MoneyMapAccountContext.Provider
            value={{
                accounts: moneyMapAccounts ?? [],
                isLoading: isLoadingAccounts,
                refetch,
            }}
        >
            {children}
        </MoneyMapAccountContext.Provider>
    );
};
